import { useEffect, useMemo, useState } from 'react'
import Header from '../components/layout/Header'
import PortfolioBillboard from '../components/portfolio/PortfolioBillboard'
import FilterTab from '../components/funding-market-risk/FilterTab'
import RiskCards from '../components/funding-market-risk/RiskCards'
import RiskTable from '../components/funding-market-risk/RiskTable'
import SearchBar from '../components/public-pools/SearchBar'
import { FUNDING_MARKET_RISK_SEO } from '../config/seo'
import {
  CATEGORY_TABS,
  TYPE_TABS,
  matchesCategory,
  matchesType,
  normalizeRiskMarket,
  sortMarkets,
} from '../data/fundingMarketRisk'
import { usePageCss } from '../hooks/usePageCss'
import { usePageMeta } from '../hooks/usePageMeta'
import { fetchOrderBookDetails, fetchTokenList } from '../lib/lighterApi'
import './funding-market-risk.css'

const FAVORITES_KEY = 'lighter:favorite-markets'

function readFavorites() {
  try {
    const raw = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]')
    return new Set(Array.isArray(raw) ? raw.map((s) => String(s).toUpperCase()) : [])
  } catch {
    return new Set()
  }
}

export default function FundingMarketRisk() {
  usePageMeta(FUNDING_MARKET_RISK_SEO)
  usePageCss('funding-market-risk-page-css', '/css/lighter_funding-market-risk.css')

  const [markets, setMarkets] = useState([])
  const [search, setSearch] = useState('')
  const [typeTab, setTypeTab] = useState('all')
  const [categoryTab, setCategoryTab] = useState('all')
  const [sortKey, setSortKey] = useState('openInterestCap')
  const [sortDirection, setSortDirection] = useState('desc')
  const [favorites, setFavorites] = useState(() => readFavorites())

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const [books, tokens] = await Promise.all([
          fetchOrderBookDetails(),
          fetchTokenList(),
        ])
        if (cancelled) return
        const tokenBySymbol = new Map(
          tokens.map((token) => [String(token.symbol || '').toUpperCase(), token])
        )
        const next = books
          .filter((row) => !row?.market_config?.hidden)
          .map((row) =>
            normalizeRiskMarket(
              row,
              tokenBySymbol.get(String(row.symbol || '').toUpperCase())
            )
          )
        setMarkets(next)
      } catch (error) {
        console.error('Failed to load funding market risk parameters', error)
      }
    }

    load()
    const interval = setInterval(load, 30_000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase()
    const filtered = markets.filter((market) => {
      if (query && !market.symbol.toLowerCase().includes(query)) return false
      if (!matchesType(market, typeTab, favorites)) return false
      if (!matchesCategory(market, categoryTab)) return false
      return true
    })
    return sortMarkets(filtered, { key: sortKey, direction: sortDirection })
  }, [markets, search, typeTab, categoryTab, favorites, sortKey, sortDirection])

  const onSort = (key) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'))
      return
    }
    setSortKey(key)
    setSortDirection(key === 'symbol' ? 'asc' : 'desc')
  }

  const onToggleFavorite = (symbol) => {
    setFavorites((prev) => {
      const next = new Set(prev)
      if (next.has(symbol)) next.delete(symbol)
      else next.add(symbol)
      localStorage.setItem(FAVORITES_KEY, JSON.stringify([...next]))
      return next
    })
  }

  return (
    <div className="funding-market-risk-page flex h-dvh w-full flex-col overflow-hidden bg-bg text-ink">
      <Header />
      <PortfolioBillboard />
      <div
        data-testid="sub-page-scroll-region"
        className="min-h-0 flex-1 overflow-y-auto"
        data-fade="y"
        style={{ '--fade-t': '0px', '--fade-b': '24px' }}
      >
        <div className="mx-auto w-full max-w-[1240px] px-8 py-10 max-mobile:px-4 max-mobile:py-6">
          <section
            className="section-in max-mobile:mb-6 mb-8"
            style={{ '--sec-d': '0s' }}
          >
            <div className="flex items-end justify-between gap-6 max-mobile:flex-col max-mobile:items-start max-mobile:gap-4">
              <div className="min-w-0">
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                  <h1 className="m-0 text-hero leading-[1.02] font-medium tracking-display [text-box-edge:cap_alphabetic] [text-box-trim:trim-start]">
                    Funding & Market Risk Parameters
                  </h1>
                </div>
              </div>
            </div>
            <div className="mt-4 max-w-[60%] text-xl/2xl text-pretty text-dim max-mobile:max-w-none">
              View funding rates, leverage limits, margin requirements, and other
              risk parameters for every market.
            </div>
          </section>

          <section className="section-in" style={{ '--sec-d': '0.06s' }}>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="flex items-stretch gap-1.5">
                {TYPE_TABS.map((tab) => (
                  <FilterTab
                    key={tab.id}
                    label={tab.label}
                    pressed={typeTab === tab.id}
                    onClick={() => setTypeTab(tab.id)}
                  />
                ))}
              </div>
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder="Search Markets"
                className="group relative flex min-w-0 overflow-hidden rounded-card ml-auto w-[200px] max-mobile:ml-0 max-mobile:w-full"
              />
            </div>
            <div className="mb-3">
              <div className="-my-2 no-scrollbar w-full max-w-full overflow-x-auto py-2">
                <div className="flex items-stretch gap-1">
                  {CATEGORY_TABS.map((tab) => (
                    <FilterTab
                      key={tab.id}
                      label={tab.label}
                      pressed={categoryTab === tab.id}
                      onClick={() => setCategoryTab(tab.id)}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="funding-risk-table-panel flex flex-col overflow-hidden rounded-panel border-[0.5px] border-line bg-fill max-mobile:border-0 max-mobile:bg-transparent max-mobile:overflow-visible">
              <RiskTable
                markets={visible}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSort={onSort}
                favorites={favorites}
                onToggleFavorite={onToggleFavorite}
              />
              <RiskCards
                markets={visible}
                favorites={favorites}
                onToggleFavorite={onToggleFavorite}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
